import { useGetCampaignEngagementApiV1Suspense } from "@/api/__generated__/api.schema";
import { CampaignUserReviewStatus } from "@/api/__generated__/models";
import { PageContainer } from "@/components/common/page-container";
import { CAMPAIGN_INFO, CAMPAIGN_NOTICES } from "@/constants/campaign";
import { routes } from "@/constants/url";
import { useWorkspaceId } from "@/hooks/auth/use-workspace";
import { useWorkspaceNavigate } from "@/lib/router/use-workspace-navigate";
import { WorkspaceLink } from "@/lib/router/workspace-link";
import { cn } from "@/lib/utils";
import { UserUploadStatusBadge } from "@/routes/campaigns/components/user-upload-status-badge";
import { YoutubeShortInfo } from "@/routes/campaigns/components/youtube-short-info";
import { useCampaignId } from "@/routes/campaigns/hooks/use-campaign-id";
import { useCampaignInfo } from "@/routes/campaigns/hooks/use-campaign-info";
import { useCurrentCampaignInfo } from "@/routes/campaigns/hooks/use-current-campaign-info";
import { formatFullDateTimeRange, refineTimezone } from "@/utils/date";
import { invariant } from "@/utils/invariant";
import {
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  List,
  ListItem,
  Separator,
  ShadowBox,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  useToastMessage,
} from "@backpac/monica";
import { Temporal } from "temporal-polyfill";

export function CampaignDetailPage() {
  return (
    <PageContainer nav={<PageContainer.Navigation title="Campaign detail" />}>
      <Contents />
    </PageContainer>
  );
}

function Contents() {
  const info = useCurrentCampaignInfo();

  if (!Object.keys(CAMPAIGN_INFO).includes(info.id)) {
    return null;
  }

  return (
    <ShadowBox className="flex flex-col gap-6 p-6">
      <div className="flex flex-col desktop:flex-row gap-4">
        <img
          className="rounded-[6px] w-full desktop:w-[160px] flex-shrink-0"
          src={info.meta?.material?.hero ?? ""}
          alt={info.title}
        />
        <div className="flex flex-col gap-2">
          <Typography size="lg" color="textPrimary" font="unbounded">
            {info.title}
          </Typography>
          <div className="flex items-center gap-2">
            <i className="i-bp-info inline-block w-4 h-4 flex-shrink-0 text-textMuted" />
            <Typography size="xs" weight={500} color="textMuted">
              {info.title}
            </Typography>
          </div>
          <div className="flex items-center gap-2">
            <i className="i-bp-calendar inline-block w-4 h-4 flex-shrink-0 text-textMuted" />
            <Typography size="xs" weight={500} color="textMuted">
              {formatFullDateTimeRange([
                Temporal.PlainDateTime.from(info.campaignUploadStartDate),
                Temporal.PlainDateTime.from(info.campaignUploadEndDate),
              ])}{" "}
              (PDT)
            </Typography>
          </div>
          <div className="flex items-center gap-2">
            <i className="i-bp-payout inline-block w-4 h-4 flex-shrink-0 text-textMuted" />
            <Typography size="xs" weight={500} color="textMuted">
              RPM ${info.rpm} (Only for US views)
            </Typography>
          </div>
        </div>
      </div>
      <Separator />
      {info.userUploadStatus === "SUBMITTED" ? <AfterSubmitContents /> : <BeforeSubmitContents />}
    </ShadowBox>
  );
}

function BeforeSubmitContents() {
  const info = useCurrentCampaignInfo();
  const estimatedUploadDate = info.estimatedUploadDate;
  const navigate = useWorkspaceNavigate();

  const videoSubmitEndDateTime = Temporal.PlainDate.from(info.campaignVideoSubmitEndDate)
    .toPlainDateTime({
      hour: 23,
      minute: 59,
    })
    .toZonedDateTime("America/Los_Angeles");

  invariant(estimatedUploadDate != null, "Campaign not registered");

  return (
    <>
      <div className="flex flex-col gap-2">
        <div className="grid grid-cols-2 gap-2">
          <Typography size="xs" weight={500} color="textSecondary">
            Upload date:
          </Typography>
          <Typography size="xs" weight={500} color="textSecondary" className="text-right">
            {Temporal.PlainDate.from(estimatedUploadDate)
              .toZonedDateTime("America/Los_Angeles")
              .toLocaleString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              })
              .replace(/at/, "")}
            {", "}
            00:00 - 23:59 (PDT)
          </Typography>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Typography size="xs" weight={500} color="textSecondary">
            My local time:
          </Typography>
          <Typography size="xs" weight={500} color="textSecondary" className="text-right">
            {refineTimezone(
              Temporal.Now.zonedDateTimeISO().toLocaleString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                timeZoneName: "long",
                hour12: false,
              }),
            )}
          </Typography>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Typography size="xs" weight={500} color="textSecondary">
            Current PDT Time:
          </Typography>
          <Typography size="xs" weight={500} color="textSecondary" className="text-right">
            {refineTimezone(
              Temporal.Now.zonedDateTimeISO("America/Los_Angeles").toLocaleString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
                hour: "numeric",
                minute: "numeric",
                hour12: false,
                timeZoneName: "long",
              }),
            )}
          </Typography>
        </div>
      </div>
      <Separator />
      <div className="space-y-4 mx-auto text-center">
        <Typography size="xs" color="textSecondary" weight={500}>
          Submission open until{" "}
          {refineTimezone(
            Temporal.ZonedDateTime.from(videoSubmitEndDateTime).toLocaleString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
              timeZoneName: "long",
            }),
          )}
        </Typography>
        <Button
          disabled={info.userUploadStatus !== "AVAILABLE"}
          onClick={() => {
            navigate(`${routes.CAMPAIGNS._videoSubmit(info.id)}`);
          }}
        >
          Submit
        </Button>
      </div>
    </>
  );
}

function AfterSubmitContents() {
  const workspaceId = useWorkspaceId();
  const campaignId = useCampaignId();
  const {
    data: {
      data: { item: engagement },
    },
  } = useGetCampaignEngagementApiV1Suspense(workspaceId, campaignId);
  const info = useCampaignInfo(campaignId);

  const campaignEnded =
    Temporal.ZonedDateTime.compare(
      Temporal.ZonedDateTime.from(`${info.campaignVideoSubmitEndDate}[America/Los_Angeles]`),
      Temporal.Now.zonedDateTimeISO("America/Los_Angeles"),
    ) < 0;

  invariant(engagement.video != null, "Campaign not registered");

  return (
    <>
      <div className="flex flex-col gap-4">
        <Typography size="sm" weight={600} color="textSecondary">
          Uploaded Short
        </Typography>
        <div className="p-4 border border-borderLight rounded-[12px]">
          <UserUploadStatusBadge
            className="flex desktop:hidden ml-auto mb-2 w-fit"
            userUploadStatus={engagement.uploadStatus}
            userReviewStatus={engagement.reviewStatus}
          />
          <YoutubeShortInfo
            shortTitle={engagement.video.title}
            shortThumbnailUrl={engagement.video.thumbnailUrl}
            shortUploadDate={engagement.video.uploadedAt}
            usedMusicTrackTitle={
              engagement.video.music ? `${engagement.video.music.track} - ${engagement.video.music.artist}` : "Unknown"
            }
            usedMusicTrackCoverImageUrl={engagement.video.music?.imageUrl ?? ""}
            rightAddon={
              <UserUploadStatusBadge
                className="hidden desktop:flex my-auto ml-auto"
                userUploadStatus={engagement.uploadStatus}
                userReviewStatus={engagement.reviewStatus}
              />
            }
          />
          <StatusGuideMessage />
        </div>
        {campaignEnded ? (
          <PreventSubmitButton>
            <Typography size="sm" weight={600} className="underline underline-offset-2">
              Resubmit
            </Typography>
          </PreventSubmitButton>
        ) : (
          <WorkspaceLink to={`${routes.CAMPAIGNS._videoSubmit(campaignId)}`} className="ml-auto">
            <Typography size="sm" weight={600} className="underline underline-offset-2">
              Resubmit
            </Typography>
          </WorkspaceLink>
        )}
      </div>
      <Separator />
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <Typography size="sm" weight={600} color="textSecondary">
            Total views & Eligible views
          </Typography>
          <Typography size="paragraphXs" weight={400} color="textMuted">
            Track your daily views and eligible views for revenue. Views from YouTube Shorts are reported based on data
            from two days prior (D-2).
          </Typography>
        </div>
        <div className="overflow-x-auto">
          <table className="table-fixed w-full border border-borderLight p-2">
            <TableBody>
              <TableRow className="bg-colorOrangeLight hover:bg-colorOrangeLight">
                <TableCell className="font-semibold border-r border-borderLight">Date</TableCell>
                <TableCell className="font-semibold border-r border-borderLight">Total Views</TableCell>
                <TableCell className="font-semibold border-r border-borderLight">Eligible Views(US)</TableCell>
                <TableCell className="font-semibold">Earnings</TableCell>
              </TableRow>
              {engagement.videoStats?.map((stat, index) => (
                <TableRow key={index}>
                  <TableCell scope="col" className="border-r border-borderLight">
                    {Temporal.PlainDate.from(stat.date).toLocaleString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </TableCell>
                  <TableCell className="border-r border-borderLight">{stat.totalView ?? "0"}</TableCell>
                  <TableCell className="border-r border-borderLight">{stat.usView ?? "0"}</TableCell>
                  <TableCell className="">{stat.revenue ?? "0"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </table>
        </div>
        <Typography asChild color="textMuted" size="xs" className="italic">
          <List>
            Why are some views not counted?
            <ListItem>
              If the campaign ended early due to reaching its goal, views after the early termination date may not be
              eligible for earnings.
            </ListItem>
            <ListItem>
              Only views within the campaign period and meeting the requirements will be counted for revenue.
            </ListItem>
          </List>
        </Typography>

        <Collapsible className="border border-borderLight rounded-[8px] p-4">
          <CollapsibleTrigger className="w-full">
            {(isOpen) => {
              return (
                <div className="flex items-center justify-between">
                  <Typography size="sm" weight={600}>
                    Compaign Guideline
                  </Typography>
                  <i
                    className={cn("inline-block i-bp-chevron-down size-4 transition-transform", isOpen && "rotate-180")}
                  />
                </div>
              );
            }}
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-4">
            {CAMPAIGN_NOTICES.map((section, index) => (
              <List key={index} className="space-y-1 mb-3">
                <Typography size="paragraphXs" weight={600}>
                  {section.title}
                </Typography>
                {section.content.map((paragraph, pIndex) => (
                  <Typography asChild key={pIndex} className="w-full text-primary" size="paragraphXs">
                    <ListItem>{paragraph}</ListItem>
                  </Typography>
                ))}
              </List>
            ))}
          </CollapsibleContent>
        </Collapsible>
      </div>
    </>
  );
}

function StatusGuideMessage() {
  const workspaceId = useWorkspaceId();
  const campaignId = useCampaignId();
  const {
    data: {
      data: { item: engagement },
    },
  } = useGetCampaignEngagementApiV1Suspense(workspaceId, campaignId);
  if (engagement.reviewStatus === CampaignUserReviewStatus.PENDING) {
    return (
      <Typography className="p-4 bg-backgroundLight rounded-[8px]" size="paragraphXs" weight={400}>
        We will review your Shorts to ensure it meets the campaign requirements. Once the review is complete, we'll
        update your submission status accordingly. Thank you for your patience.
      </Typography>
    );
  }
  if (engagement.reviewStatus === CampaignUserReviewStatus.APPROVED) {
    const reviewDateString = engagement.reviewedAt
      ? `${Temporal.Instant.from(engagement.reviewedAt)
          .toZonedDateTimeISO("America/Los_Angeles")
          .toLocaleString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })} (PDT)`
      : "";

    return (
      <Typography className="p-4 bg-backgroundLight rounded-[8px]" size="paragraphXs" weight={500}>
        Review completion date: {reviewDateString}
      </Typography>
    );
  }
  if (engagement.reviewStatus === CampaignUserReviewStatus.REJECTED) {
    const reviewDateString = engagement.reviewedAt
      ? `${Temporal.Instant.from(engagement.reviewedAt)
          .toZonedDateTimeISO("America/Los_Angeles")
          .toLocaleString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })} (PDT)`
      : "";

    return (
      <Typography className="p-4 bg-backgroundLight rounded-[8px]" size="paragraphXs" weight={400}>
        Your submission did not meet the campaign requirements.
        <br />
        Reason for rejection: {engagement.rejectedReason}
        <Typography size="xs" weight={500} className="mt-2">
          Review completion date: {reviewDateString}
        </Typography>
      </Typography>
    );
  }
}

function PreventSubmitButton({ children }: { children: React.ReactNode }) {
  const { info } = useToastMessage();
  return (
    <button
      type="button"
      className="ml-auto"
      onClick={() => info("The review has ended. Video submissions are closed.")}
    >
      {children}
    </button>
  );
}
